import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import {
  doc,
  getDoc,
  query,
  collection,
  where,
  getDocs,
  limit,
} from "firebase/firestore";
import { db } from "../config/firebaseConfig";
import NavBar from "../components/NavBar";
import LoadingPage from "../components/InitialLoadingScreen";
import styles from "../styling/userPage.module.css";

function UserPage() {
  const { uid } = useParams();
  const [userData, setUserData] = useState(null);
  const [similarProfiles, setSimilarProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!uid) {
      setError("No user ID provided.");
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      try {
        // Fetch main user data
        const userDocRef = doc(db, "adonis_users", uid);
        const userSnapshot = await getDoc(userDocRef);

        if (!userSnapshot.exists()) {
          setError("User not found.");
          return;
        }

        const userData = userSnapshot.data();
        setUserData(userData);

        // Fetch similar profiles based on industry and school
        const similarProfilesQuery = query(
          collection(db, "adonis_users"),
          where("formData.industry", "==", userData.formData.industry),
          where("formData.searchable", "==", true),
          limit(5)
        );

        const similarProfilesSnapshot = await getDocs(similarProfilesQuery);
        const similarProfilesData = similarProfilesSnapshot.docs
          .filter((doc) => doc.id !== uid) // Exclude current user
          .map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));

        setSimilarProfiles(similarProfilesData);
      } catch (err) {
        console.error("Error fetching data:", err);
        setError("Failed to load data.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [uid]);

  const handleEmailClick = () => {
    if (userData?.formData?.email && userData?.formData?.name) {
      const email = userData.formData.email;
      const name = userData.formData.name;

      // Email template
      const subject = encodeURIComponent("Let's Connect! | Hatch");
      const body = encodeURIComponent(
        `Hi ${name},\n\nI saw your profile on Hatch and wanted to reach out to... \n\nBest regards, \nYour Name`
      );

      // Construct the mailto link
      window.location.href = `mailto:${email}?subject=${subject}&body=${body}`;
    }
  };

  if (loading) {
    return <LoadingPage />;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  if (!userData) {
    return null;
  }

  const { formData, resumeDetails } = userData;

  return (
    <>
      <NavBar />
      <div className={styles["page-layout"]}>
        <div className={styles["main-content"]}>
          <div className={styles["profile-container"]}>
            <div className={styles["profile-header"]}>
              <div className={styles["profile-header-content"]}>
                <div className={styles["name-and-actions"]}>
                  <h1>{formData?.name || "N/A"}</h1>
                  <div className={styles["action-buttons"]}>
                    {formData?.linkedin && (
                      <a
                        href={formData.linkedin}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles["linkedin-button"]}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                        >
                          <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
                        </svg>
                        LinkedIn
                      </a>
                    )}
                    <button
                      className={styles["email-button"]}
                      onClick={handleEmailClick}
                      disabled={!formData?.email}
                      title={formData?.email || "Email not available"}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                        <polyline points="22,6 12,13 2,6" />
                      </svg>
                      Email
                    </button>
                  </div>
                </div>
                <p className={styles["profile-description"]}>
                  {formData?.description || "No description provided"}
                </p>
                <div className={styles["profile-meta"]}>
                  <span>{formData?.email || "N/A"}</span>
                  <span>•</span>
                  <span>{formData?.industry || "N/A"}</span>
                  {formData?.company && (
                    <>
                      <span>•</span>
                      <span>{formData.company}</span>
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className={styles["profile-sections"]}>
              {formData?.externalLinks?.length > 0 && (
                <div className={styles["section"]}>
                  <h2>External Links</h2>
                  <div className={styles["external-links"]}>
                    {formData.externalLinks.map((link, index) => (
                      <a
                        key={index}
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles["external-link"]}
                      >
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                        {link.title}
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {resumeDetails?.education?.length > 0 && (
                <div className={styles["section"]}>
                  <h2>Education</h2>
                  <div className={styles["section-content"]}>
                    {resumeDetails.education.map((edu, index) => (
                      <div key={index} className={styles["education-item"]}>
                        <h3>{edu.school}</h3>
                        <p>
                          {edu.degree} in {edu.major}
                        </p>
                        <p className={styles["meta"]}>{edu.graduation_year}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {resumeDetails?.work_experience?.length > 0 && (
                <div className={styles["section"]}>
                  <h2>Work Experience</h2>
                  <div className={styles["section-content"]}>
                    {resumeDetails.work_experience.map((work, index) => (
                      <div key={index} className={styles["experience-item"]}>
                        <h3>{work.title}</h3>
                        <p>{work.company}</p>
                        <p className={styles["meta"]}>{work.years}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {resumeDetails?.skills?.length > 0 && (
                <div className={styles["section"]}>
                  <h2>Skills & Expertise</h2>
                  <div className={styles["tags-container"]}>
                    {resumeDetails.skills.map((skill, index) => (
                      <span key={index} className={styles["tag"]}>
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {(resumeDetails?.certifications?.length > 0 ||
                resumeDetails?.languages?.length > 0) && (
                <div className={styles["section"]}>
                  <h2>Awards</h2>
                  {resumeDetails?.certifications?.length > 0 && (
                    <div className={styles["subsection"]}>
                      <h3>Certifications & Awards</h3>
                      <div className={styles["tags-container"]}>
                        {resumeDetails.certifications.map((cert, index) => (
                          <span key={index} className={styles["tag"]}>
                            {cert}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {resumeDetails?.languages?.length > 0 && (
                    <div className={styles["subsection"]}>
                      <h3>Languages</h3>
                      <div className={styles["tags-container"]}>
                        {resumeDetails.languages.map((lang, index) => (
                          <span key={index} className={styles["tag"]}>
                            {lang}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className={styles["sidebar"]}>
          <div className={styles["similar-profiles"]}>
            <h2>Similar Profiles</h2>
            {similarProfiles.length > 0 ? (
              <div className={styles["similar-profiles-list"]}>
                {similarProfiles.map((profile) => (
                  <Link
                    key={profile.id}
                    to={`/profile/${profile.id}`}
                    className={styles["similar-profile-card"]}
                  >
                    <div className={styles["similar-profile-info"]}>
                      <h3>{profile.formData.name}</h3>
                      <p className={styles["similar-profile-meta"]}>
                        {profile.formData.industry}
                      </p>
                    </div>
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      className={styles["arrow-icon"]}
                    >
                      <path d="M5 12h14M12 5l7 7-7 7" />
                    </svg>
                  </Link>
                ))}
              </div>
            ) : (
              <p className={styles["empty-state"]}>No similar profiles found</p>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default UserPage;
